import React from 'react';
import { motion } from 'framer-motion';
import { FaApple, FaGooglePlay, FaMobileAlt, FaCheckCircle } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import { APP_STORE_URL, PLAY_STORE_URL } from '../config/apiConfig';

const DownloadPage = () => {
  const [ref1, inView1] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ref2, inView2] = useInView({ threshold: 0.1, triggerOnce: true });

  const features = [
    'Track your workouts and progress',
    'Connect with certified coaches',
    'Personalized training plans',
    'Nutrition guidance and meal plans',
    'Community support and motivation',
    'Real-time workout tracking',
  ];

  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank');
  };

  const handlePlayStoreClick = () => {
    window.open(PLAY_STORE_URL, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-600/20 blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-block mb-6"
            >
              <FaMobileAlt className="text-primary text-6xl md:text-8xl" />
            </motion.div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Download <span className="text-gradient">MyFitness</span> App
            </h1>
            
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              Take your fitness journey to the next level. Available on iOS and Android.
            </p>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <motion.button
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAppStoreClick}
                className="group relative w-full sm:w-auto bg-white text-black px-8 py-4 rounded-xl font-semibold flex items-center justify-center space-x-3 shadow-2xl hover:shadow-primary/50 transition-all duration-300"
              >
                <FaApple className="text-3xl" />
                <div className="text-left">
                  <p className="text-xs text-gray-600">Download on the</p>
                  <p className="text-lg font-bold">App Store</p>
                </div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePlayStoreClick}
                className="group relative w-full sm:w-auto bg-gradient-to-r from-primary to-purple-600 text-white px-8 py-4 rounded-xl font-semibold flex items-center justify-center space-x-3 shadow-2xl hover:shadow-primary/50 transition-all duration-300"
              >
                <FaGooglePlay className="text-3xl" />
                <div className="text-left">
                  <p className="text-xs">GET IT ON</p>
                  <p className="text-lg font-bold">Google Play</p>
                </div>
              </motion.button>
            </div>

            {/* QR Code Section */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="inline-block bg-white p-6 rounded-2xl shadow-2xl"
            >
              <p className="text-black font-semibold mb-4">Scan to Download</p>
              <div className="w-48 h-48 bg-gray-200 rounded-xl flex items-center justify-center">
                {/* Replace with actual QR code */}
                <p className="text-gray-500 text-sm text-center px-4">QR Code<br/>Coming Soon</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={ref1} className="py-20 bg-black/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView1 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Why Choose <span className="text-gradient">MyFitness</span>?
            </h2>
            <p className="text-gray-400 text-lg">
              Everything you need to achieve your fitness goals
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView1 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-gray-700 hover:border-primary transition-all duration-300"
              >
                <FaCheckCircle className="text-primary text-2xl mb-4" />
                <p className="text-white font-medium">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots Section */}
      <section ref={ref2} className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView2 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Beautiful & <span className="text-gradient">Intuitive</span> Design
            </h2>
            <p className="text-gray-400 text-lg">
              Experience fitness like never before
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView2 ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="relative aspect-[9/16] bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl overflow-hidden border-4 border-gray-700"
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-gray-500 text-lg">Screenshot {item}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/20 to-purple-600/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Download the app now and get started with your personalized fitness plan
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAppStoreClick}
                className="btn-primary flex items-center justify-center space-x-2"
              >
                <FaApple size={24} />
                <span>App Store</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePlayStoreClick}
                className="btn-secondary flex items-center justify-center space-x-2"
              >
                <FaGooglePlay size={24} />
                <span>Google Play</span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DownloadPage;
